import API from "../core/api/typed/api";
import { type CasingIgnorePaths } from "../core/api/typed/drf";
import { TimestampedModel } from "../resources/modelTypes";
import React, {
  createContext,
  FunctionComponent,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type ModelContextValue<T extends TimestampedModel> = {
  commit: () => Promise<T>;
  create: (data: Omit<T, "id" | "createdAt" | "updatedAt">) => Promise<T>;
  discard: () => void;
  isLoading: boolean;
  items: T[];
  refresh: () => Promise<void>;
  remove: (id: number) => Promise<void>;
  replaceItem: (item: T) => void;
  stage: (delta: Partial<T>) => void;
  staged: Partial<T> | null;
  update: (id: number, data: Partial<T>) => Promise<T>;
};

export const MODEL_API_PATHS = {
  Comments: "comments",
  Event: "events",
  Pages: "pages",
  Section: "sections",
  Site: "sites",
} as const;

type ModelApiKey = keyof typeof MODEL_API_PATHS;

/**
 * Creates a React Context that manages a list of model instances of type T.
 * Provides CRUD operations plus a staged-update pattern (stage → commit/discard).
 *
 * @param model - Key from MODEL_API_PATHS (e.g. "Site"). Used to resolve the DRF resource path.
 * @param ignorePaths - Key paths exempt from snake_case / camelCase conversion (e.g. ["themeDataOverrides"]
 *   for JSON blobs whose nested keys must not be transformed).
 */
function createModelContext<T extends TimestampedModel>(
  model: ModelApiKey,
  ignorePaths?: CasingIgnorePaths,
): {
  Provider: FunctionComponent<{ children: ReactNode; initialItems: T[] }>;
  useModel: () => ModelContextValue<T>;
} {
  const apiPath = MODEL_API_PATHS[model];
  const Context = createContext<ModelContextValue<T> | null>(null);

  const ModelProvider = ({
    children,
    initialItems,
  }: {
    children: ReactNode;
    initialItems: T[];
  }) => {
    const [serverItems, setServerItems] = useState<T[]>(initialItems);
    const [staged, setStaged] = useState<Partial<T> | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Errors are not caught here — rejections propagate from the returned Promise.
    // Each consumer is responsible for its own error handling via .catch().
    // apiPath is a module-level constant (not React state), and all state mutations use
    // functional updater form, so these callbacks safely use empty dependency arrays.
    const refresh = useCallback(async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await API.get(apiPath);
        setServerItems(response as T[]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    const create = useCallback(
      async (data: Omit<T, "id" | "createdAt" | "updatedAt">): Promise<T> => {
        setIsLoading(true);
        try {
          const response = await API.post(apiPath, {}, data, ignorePaths);
          const newItem = response as T;
          setServerItems((prev) => [...prev, newItem]);
          return newItem;
        } finally {
          setIsLoading(false);
        }
      },
      [],
    );

    const update = useCallback(
      async (id: number, data: Partial<T>): Promise<T> => {
        setIsLoading(true);
        try {
          const response = await API.patch(
            `${apiPath}/${id}`,
            {},
            data,
            ignorePaths,
          );
          const updatedItem = response as T;
          setServerItems((prev) =>
            prev.map((item) => (item.id === id ? updatedItem : item)),
          );
          return updatedItem;
        } finally {
          setIsLoading(false);
        }
      },
      [],
    );

    const remove = useCallback(async (id: number): Promise<void> => {
      setIsLoading(true);
      try {
        await API.delete(`${apiPath}/${id}`, null, {});
        setServerItems((prev) => prev.filter((item) => item.id !== id));
      } finally {
        setIsLoading(false);
      }
    }, []);

    const stage = useCallback((delta: Partial<T>): void => {
      setStaged((prev) => ({ ...(prev ?? {}), ...delta }) as Partial<T>);
    }, []);

    const discard = useCallback((): void => {
      setStaged(null);
    }, []);

    const commit = useCallback(async (): Promise<T> => {
      if (!staged) {
        throw new Error(`No staged changes to commit for "${apiPath}"`);
      }
      if (staged.id == null) {
        throw new Error(
          `Cannot commit staged changes without an id for "${apiPath}"`,
        );
      }
      setIsLoading(true);
      try {
        const response = await API.patch(
          `${apiPath}/${staged.id}`,
          {},
          staged,
          ignorePaths,
        );
        const updatedItem = response as T;
        setServerItems((prev) =>
          prev.map((item) => (item.id === staged.id ? updatedItem : item)),
        );
        setStaged(null);
        return updatedItem;
      } finally {
        setIsLoading(false);
      }
    }, [staged]);

    const replaceItem = useCallback((item: T): void => {
      setServerItems((prev) =>
        prev.map((existing) => (existing.id === item.id ? item : existing)),
      );
    }, []);

    const items = useMemo(
      () =>
        serverItems.map((item) =>
          item.id === staged?.id ? { ...item, ...staged } : item,
        ),
      [serverItems, staged],
    );

    const contextValue = useMemo(
      () => ({
        commit,
        create,
        discard,
        isLoading,
        items,
        refresh,
        remove,
        replaceItem,
        stage,
        staged,
        update,
      }),
      [
        commit,
        create,
        discard,
        isLoading,
        items,
        refresh,
        remove,
        replaceItem,
        stage,
        staged,
        update,
      ],
    );

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
  };

  const Provider = memo(ModelProvider);

  const useModel = (): ModelContextValue<T> => {
    const context = useContext(Context);
    if (!context) {
      throw new Error(
        `useModel must be called within its Provider (apiPath: "${apiPath}")`,
      );
    }
    return context;
  };

  return { Provider, useModel };
}

export default createModelContext;
